import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/i18n";
import api from "@/lib/api";
import { toast } from "sonner";
import { Compass, Loader2, Sparkles, Save } from "lucide-react";

const L = (lang, zh, en) => (lang === "zh" ? zh : en);

export const DailyHexagram = ({ autoLoad = false }) => {
  const { lang } = useI18n();
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);

  const cast = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get("/numerology/daily-hexagram", { params: { lang } });
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  }, [lang]);

  useEffect(() => { if (autoLoad) cast(); }, [autoLoad, cast]);

  const save = async () => {
    try {
      await api.post("/numerology/save", { kind: "daily", result: data.result, ai_reading: data.ai_reading, label: `${L(lang, "今日一卦", "Daily")} ${data.date || ""}` });
      toast.success(L(lang, "已保存到我的记录", "Saved"));
    } catch (e) { toast.error("Error"); }
  };

  const r = data?.result;
  return (
    <Card className="border-border bg-card" data-testid="daily-hexagram">
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="flex items-center gap-2">
            <span className="grid h-7 w-7 place-items-center rounded-full bg-[hsl(var(--brand-gold)/0.16)] text-[hsl(var(--brand-bronze))]"><Compass className="h-4 w-4" /></span>
            <span className="font-display font-semibold text-[hsl(var(--brand-ink))]">{L(lang, "今日指引 · 一卦", "Today's Guidance")}</span>
          </div>
          {!r && (
            <Button size="sm" onClick={cast} disabled={busy} data-testid="daily-hex-cast" className="bg-[hsl(var(--primary))] text-primary-foreground">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : L(lang, "一键卜今日", "Cast")}
            </Button>
          )}
        </div>

        {busy && !r && <div className="py-6 text-center text-muted-foreground flex flex-col items-center gap-2 text-sm"><Loader2 className="h-5 w-5 animate-spin text-[hsl(var(--brand-bronze))]" />{L(lang, "起卦解卦中…", "Casting…")}</div>}

        {r && (
          <div data-testid="daily-hex-result">
            {data.ai_reading?.guidance_one_liner && (
              <div className="mb-3 rounded-lg border border-[hsl(var(--brand-gold))] bg-[hsl(var(--brand-gold-soft)/0.28)] p-3 flex items-center gap-2" data-testid="daily-hex-guidance">
                <Sparkles className="h-4 w-4 text-[hsl(var(--brand-bronze))] shrink-0" />
                <span className="text-sm font-semibold text-[hsl(var(--brand-ink))]">{L(lang, "今日一句：", "Today: ")}{data.ai_reading.guidance_one_liner}</span>
              </div>
            )}
            <div className="flex items-center justify-center gap-3 text-center">
              <div className="rounded-lg border border-border px-3 py-2">
                <div className="text-2xl leading-none">{r.ben_gua.upper.symbol}<br />{r.ben_gua.lower.symbol}</div>
                <div className="mt-1 font-display font-bold text-[hsl(var(--brand-ink))]">{r.ben_gua.name}</div>
              </div>
              <div className="text-xs text-muted-foreground">
                <div className="text-[hsl(var(--brand-vermillion))] font-bold">{L(lang, `动爻 ${r.moving_line}`, `Line ${r.moving_line}`)}</div>
                <div className="text-xl">→</div>
              </div>
              <div className="rounded-lg border border-border px-3 py-2">
                <div className="text-2xl leading-none">{r.bian_gua.upper.symbol}<br />{r.bian_gua.lower.symbol}</div>
                <div className="mt-1 font-display font-bold text-[hsl(var(--brand-ink))]">{r.bian_gua.name}</div>
              </div>
            </div>
            {data.ai_reading?.summary && (
              <div className="mt-3 rounded-lg border border-[hsl(var(--brand-gold))] bg-[hsl(var(--brand-gold-soft)/0.2)] p-3">
                <div className="flex items-center gap-1.5 mb-1 text-[hsl(var(--brand-bronze))]"><Sparkles className="h-3.5 w-3.5" /><span className="text-xs font-semibold">{L(lang, "今日解读", "Reading")}</span></div>
                <p className="text-sm leading-relaxed text-foreground">{data.ai_reading.summary}</p>
              </div>
            )}
            <div className="mt-2 flex justify-end">
              <Button size="sm" variant="outline" onClick={save} data-testid="daily-hex-save" className="border-[hsl(var(--brand-gold))] text-[hsl(var(--brand-bronze))]"><Save className="h-4 w-4 mr-1" />{L(lang, "保存", "Save")}</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
